import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Sparkles,
  Search,
  MessageSquare,
  MessageCircle,
  Bell,
  Calendar,
  CalendarClock,
  ClipboardList,
  FileText,
  Folder,
  Hammer,
  LayoutDashboard,
  Mail,
  Map,
  PenTool,
  Receipt,
  Rocket,
  Settings,
  Trophy,
  Truck,
  Users,
  Wrench,
  Zap,
  BarChart3,
  CheckCircle2,
  DollarSign,
  type LucideIcon,
} from 'lucide-react';
import { ReleaseAnnouncement, ReleaseFeature, UserRole, EmployeeDesignation } from '../../types';
import { parseReleaseDescriptionBullets } from '../../utils/releaseDescriptionBullets';
import {
  RELEASE_AREA_OPTIONS,
  DEFAULT_RELEASE_AREA,
  iconForReleaseArea,
  isGenericReleaseTitle,
  followWhatsNewTitle,
} from '../../utils/releaseAreaOptions';
import { hasReleaseAudienceSelection } from '../../utils/releaseAudience';
import ReleaseAudiencePicker from './ReleaseAudiencePicker';
import './ReleaseAnnouncementPopup.css';

function featureBodyToEditableText(feature: ReleaseFeature, bullets: string[]): string {
  const description = (feature.description || '').trim();
  if (bullets.length > 0) {
    if (description && !bullets.includes(description)) {
      return `${description}\n\n${bullets.join('\n')}`;
    }
    return bullets.join('\n');
  }
  return description;
}

/**
 * Curated icon lookup so release docs can reference lucide icons by name
 * without pulling the entire icon set into the bundle.
 */
const FEATURE_ICONS: Record<string, LucideIcon> = {
  Sparkles,
  Search,
  MessageSquare,
  MessageCircle,
  Bell,
  Calendar,
  CalendarClock,
  ClipboardList,
  FileText,
  Folder,
  Hammer,
  LayoutDashboard,
  Mail,
  Map,
  PenTool,
  Receipt,
  Rocket,
  Settings,
  Trophy,
  Truck,
  Users,
  Wrench,
  Zap,
  BarChart3,
  CheckCircle2,
  DollarSign,
};

interface ReleaseAnnouncementPopupProps {
  release: ReleaseAnnouncement;
  onDismiss: () => void;
  /** Admin publish flow — shows Publish / Skip / Later instead of "Got it". */
  mode?: 'user' | 'admin';
  onPublish?: () => void;
  onSkip?: () => void;
  publishing?: boolean;
  audienceRoles?: UserRole[];
  audienceDesignations?: EmployeeDesignation[];
  audienceUserIds?: string[];
  onAudienceChange?: (roles: UserRole[]) => void;
  onAudienceDesignationsChange?: (designations: EmployeeDesignation[]) => void;
  onAudienceUserIdsChange?: (userIds: string[]) => void;
  features?: ReleaseFeature[];
  onFeaturesChange?: (features: ReleaseFeature[]) => void;
  onTitleChange?: (title: string) => void;
}

/**
 * "What's New" popup shown once per user after a release is published.
 * Animated LCTF logo, version badge, and staggered feature rows with
 * icons or small screenshots.
 */
const ReleaseAnnouncementPopup: React.FC<ReleaseAnnouncementPopupProps> = ({
  release,
  onDismiss,
  mode = 'user',
  onPublish,
  onSkip,
  publishing = false,
  audienceRoles = [],
  audienceDesignations = [],
  audienceUserIds = [],
  onAudienceChange,
  onAudienceDesignationsChange,
  onAudienceUserIdsChange,
  features: featuresProp,
  onFeaturesChange,
  onTitleChange,
}) => {
  const isAdmin = mode === 'admin';
  const canPublish =
    !isAdmin || hasReleaseAudienceSelection(audienceRoles, audienceDesignations, audienceUserIds);
  const displayFeatures = featuresProp ?? release.features;

  const updateFeature = (index: number, patch: Partial<ReleaseFeature>) => {
    if (!onFeaturesChange) return;
    const next = displayFeatures.map((feature, i) =>
      i === index ? { ...feature, ...patch } : feature,
    );
    onFeaturesChange(next);
  };

  const handleAreaChange = (index: number, area: string) => {
    const previousArea = displayFeatures[index]?.area;
    updateFeature(index, {
      area,
      icon: iconForReleaseArea(area),
    });
    if (onTitleChange) {
      onTitleChange(followWhatsNewTitle(release.title, previousArea, area));
    }
  };

  const addFeature = () => {
    if (!onFeaturesChange) return;
    onFeaturesChange([
      ...displayFeatures,
      {
        title: 'New update',
        description: '',
        area: DEFAULT_RELEASE_AREA.area,
        icon: DEFAULT_RELEASE_AREA.icon,
      },
    ]);
  };

  const removeFeature = (index: number) => {
    if (!onFeaturesChange || displayFeatures.length <= 1) return;
    onFeaturesChange(displayFeatures.filter((_, i) => i !== index));
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    },
    [onDismiss],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const areaOptions = (() => {
    const known = new Set(RELEASE_AREA_OPTIONS.map((option) => option.area));
    const extras = displayFeatures
      .map((feature) => feature.area)
      .filter((area): area is string => !!area && !known.has(area));
    return [
      ...RELEASE_AREA_OPTIONS,
      ...extras.map((area) => ({ area, icon: iconForReleaseArea(area) })),
    ];
  })();

  return createPortal(
    <div
      className="release-popup-overlay"
      onClick={isAdmin ? undefined : onDismiss}
      role="dialog"
      aria-modal="true"
      aria-label={
        isAdmin
          ? `Publish release notes for version ${release.version}`
          : `What's new in version ${release.version}`
      }
    >
      <div
        className={`release-popup${isAdmin ? ' release-popup--admin' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          className="release-popup-close"
          onClick={onDismiss}
          aria-label="Dismiss announcement"
        >
          <X size={18} />
        </button>

        <div className="release-popup-hero">
          <div className="release-logo-wrap">
            <span className="release-logo-ring" aria-hidden="true" />
            <span className="release-logo-ring release-logo-ring--delayed" aria-hidden="true" />
            <img
              src="/assets/logo/LCTF Profile.png"
              alt="LC Timber Frames"
              className="release-logo"
              onError={e => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <div className="release-version-badge">
            <Sparkles size={13} />
            <span>v{release.version}</span>
          </div>
          {isAdmin && onTitleChange ? (
            <label className="release-popup-title-field">
              <span className="release-popup-title-label">Headline staff will see</span>
              <input
                type="text"
                className="release-popup-title-edit"
                value={release.title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder="What's new in the CRM"
                aria-label="Release headline"
              />
            </label>
          ) : (
            <h2 className="release-popup-title">{release.title}</h2>
          )}
          <p className="release-popup-subtitle">
            {isAdmin
              ? 'Write what staff should know. Change the area if this is not a backend-only update — the headline follows until you type your own.'
              : 'Here\u2019s what\u2019s new in the CRM'}
          </p>
        </div>

        <div className="release-features">
          {displayFeatures.map((feature, index) => {
            const Icon = (feature.icon && FEATURE_ICONS[feature.icon]) || Sparkles;
            const bullets = parseReleaseDescriptionBullets(feature.description, feature.bullets);
            const showTitle =
              !!feature.title &&
              !isGenericReleaseTitle(feature.title, feature.area) &&
              (displayFeatures.length > 1 || bullets.length === 0);
            const canEdit = isAdmin && !!onFeaturesChange;
            const notesDraft = featureBodyToEditableText(feature, bullets);

            const notesValue = canEdit && !(feature.bullets && feature.bullets.length)
              ? (feature.description || '')
              : notesDraft;

            return (
              <div
                key={`release-section-${index}`}
                className={`release-feature ${canEdit ? 'release-feature--composer' : ''}`}
                style={{ animationDelay: `${0.45 + index * 0.1}s` }}
              >
                {feature.imageUrl ? (
                  <img
                    src={feature.imageUrl}
                    alt=""
                    className="release-feature-thumb"
                    loading="lazy"
                    onError={e => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <span className="release-feature-icon">
                    <Icon size={18} />
                  </span>
                )}
                <div className="release-feature-body">
                  <div className="release-feature-heading">
                    {canEdit ? (
                      <>
                        <select
                          className="release-feature-area-select"
                          value={feature.area ?? DEFAULT_RELEASE_AREA.area}
                          onChange={(e) => handleAreaChange(index, e.target.value)}
                          aria-label="CRM area"
                        >
                          {areaOptions.map((option) => (
                            <option key={option.area} value={option.area}>
                              {option.area}
                            </option>
                          ))}
                        </select>
                        {displayFeatures.length > 1 && (
                          <button
                            type="button"
                            className="release-feature-remove"
                            onClick={() => removeFeature(index)}
                          >
                            Remove
                          </button>
                        )}
                      </>
                    ) : (
                      feature.area && (
                        <span className="release-feature-area">{feature.area}</span>
                      )
                    )}
                  </div>

                  {canEdit ? (
                    <>
                      <input
                        type="text"
                        className="release-feature-title-edit"
                        value={feature.title}
                        onChange={(e) => updateFeature(index, { title: e.target.value })}
                        placeholder="Section title"
                        aria-label="Section title"
                      />
                      <label className="release-feature-notes-label">
                        Notes for staff
                        <textarea
                          className="release-feature-edit"
                          value={notesValue}
                          onChange={(e) => updateFeature(index, { description: e.target.value, bullets: [] })}
                          rows={Math.max(8, notesValue.split('\n').length + 2)}
                          placeholder="Write the full update in plain language. One idea per line becomes a bullet; a paragraph stays a paragraph."
                          aria-label="Staff release notes"
                        />
                      </label>
                    </>
                  ) : (
                    <>
                      {showTitle && (
                        <div className="release-feature-title-row">
                          <span className="release-feature-title">{feature.title}</span>
                        </div>
                      )}
                      {bullets.length > 1 ? (
                        <ul className="release-feature-bullets">
                          {bullets.map((item, bulletIndex) => (
                            <li key={`${item}-${bulletIndex}`}>{item}</li>
                          ))}
                        </ul>
                      ) : feature.description ? (
                        <p className="release-feature-description">{feature.description}</p>
                      ) : bullets.length === 1 ? (
                        <p className="release-feature-description">{bullets[0]}</p>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {isAdmin && onFeaturesChange && (
            <button type="button" className="release-feature-add-section" onClick={addFeature}>
              Add another section
            </button>
          )}
        </div>

        {isAdmin && onAudienceChange && (
          <ReleaseAudiencePicker
            selected={audienceRoles}
            selectedDesignations={audienceDesignations}
            selectedUserIds={audienceUserIds}
            onChange={onAudienceChange}
            onDesignationsChange={onAudienceDesignationsChange}
            onUserIdsChange={onAudienceUserIdsChange}
            disabled={publishing}
          />
        )}

        <div className={`release-popup-footer ${isAdmin ? 'release-popup-footer--admin' : ''}`}>
          {isAdmin ? (
            <>
              <button
                type="button"
                className="release-publish-secondary"
                onClick={onSkip}
                disabled={publishing}
              >
                Skip this release
              </button>
              <button
                type="button"
                className="release-publish-secondary"
                onClick={onDismiss}
                disabled={publishing}
              >
                Remind me later
              </button>
              <button
                type="button"
                className="release-got-it release-publish-primary"
                onClick={onPublish}
                disabled={publishing || !canPublish}
                title={canPublish ? undefined : 'Select at least one audience group'}
              >
                {publishing ? 'Publishing…' : 'Publish release notes'}
              </button>
            </>
          ) : (
            <button type="button" className="release-got-it" onClick={onDismiss}>
              Got it
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReleaseAnnouncementPopup;
